import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { Phone, Plus, Settings2, RefreshCw, Trash2 } from 'lucide-react';
import { usePhoneNumbers } from '../../hooks/phone/usePhoneNumbers';
import { PhoneNumber } from '../../types/phone-numbers';
import SearchPhoneDialog from '../../components/phone/SearchPhoneDialog';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { ROUTES } from '../../config/constants/routes';

const getCountryFlag = (countryCode: string): string => {
  const codePoints = [...countryCode.toUpperCase()].map(c => 0x1f1e6 - 65 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

interface PhoneNumbersTabProps {
  subaccountSid: string;
}

const PhoneNumbersTab = ({ subaccountSid }: PhoneNumbersTabProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { phoneNumbers, isLoading, fetchAll, release } = usePhoneNumbers();

  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PhoneNumber | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  useEffect(() => {
    if (subaccountSid) {
      fetchAll().catch(() => {
        setNotification({
          open: true,
          message: 'Failed to load phone numbers.',
          severity: SEVERITY.ERROR,
        });
      });
    }
  }, [fetchAll, subaccountSid]);

  const handlePurchased = () => {
    setSearchDialogOpen(false);
    setNotification({
      open: true,
      message: 'Phone number purchased successfully.',
      severity: SEVERITY.SUCCESS,
    });
    fetchAll();
  };

  const handleConfigureRouting = (phoneNumber: PhoneNumber) => {
    navigate(ROUTES.PHONE_ROUTING(phoneNumber.id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await release(deleteTarget.id);
      setNotification({
        open: true,
        message: `Phone number ${deleteTarget.e164Number} released successfully.`,
        severity: SEVERITY.SUCCESS,
      });
      fetchAll();
    } catch {
      setNotification({
        open: true,
        message: 'Failed to release phone number.',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  // Phone numbers management
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: 3,
          gap: 1,
        }}
      >
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => fetchAll()} disabled={isLoading}>
            {isLoading ? <CircularProgress size={18} /> : <RefreshCw size={18} />}
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          disableElevation
          startIcon={<Plus size={16} />}
          onClick={() => setSearchDialogOpen(true)}
        >
          Search &amp; Buy Numbers
        </Button>
      </Box>

      {isLoading && phoneNumbers.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : phoneNumbers.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Phone size={24} color={theme.palette.primary.main} />
          </Box>
          <Typography variant="h6" gutterBottom>
            No phone numbers yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Search for and purchase phone numbers to get started
          </Typography>
          <Button
            variant="contained"
            disableElevation
            startIcon={<Plus size={16} />}
            onClick={() => setSearchDialogOpen(true)}
          >
            Search &amp; Buy Numbers
          </Button>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Friendly Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {phoneNumbers.map(number => {
                return (
                  <TableRow
                    key={number.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                        {number.e164Number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <span style={{ fontSize: '1.1rem' }}>{getCountryFlag(number.country)}</span>
                        <Typography variant="body2">{number.country}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={number.friendlyName ? 'text.primary' : 'text.disabled'}
                      >
                        {number.friendlyName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Configure Routing">
                        <IconButton
                          size="small"
                          onClick={() => handleConfigureRouting(number)}
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <Settings2 size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Release Number">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(number)}
                          sx={{ color: theme.palette.error.main, ml: 0.5 }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SearchPhoneDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onPurchased={handlePurchased}
        subaccountSid={subaccountSid}
      />

      <ConfirmationDialog
        open={!!deleteTarget}
        title="Release Phone Number"
        message={`Are you sure you want to release ${deleteTarget?.e164Number}? This will remove the number from your account and it cannot be undone. Please note that once released, this number cannot be repurchased for at least 10 days.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        isLoading={isDeleting}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default PhoneNumbersTab;
